import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ReportsPanel, StatsPanel } from "./admin/AdminStats";
import { AudioPanel } from "./admin/AdminAudio";
import { useMe } from "../hooks/useMe";
import { api, ApiError, errorMessage } from "../lib/api";
import { formatMYR } from "../lib/format";
import { Alert, Button } from "../components/Form";
import { CardGrid, FeatureCard, PageHeader } from "../components/FeatureCard";

type AdminOrder = {
  id: string;
  reference_code: string;
  buyer_email: string;
  price_tier: string;
  amount_due_sen: number;
  payer_reference: string | null;
  payer_name: string | null;
  submitted_at: string | null;
  created_at: string;
  status: string;
  payment_proof_key?: string | null;
};
type Stats = { tier1_sold: number; tier2_sold: number; early_held: number; awaiting_verification: number };

const dt = (s: string | null) =>
  s ? new Date(s).toLocaleString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" }) : "—";

export function AdminPage() {
  const { me, loading } = useMe();
  if (loading) return <p className="text-sm text-muted">Memuatkan…</p>;
  if (!me) return <Alert kind="info">Sila <Link to="/login?next=/admin" className="underline">log masuk</Link>.</Alert>;
  if (me.role !== "admin") return <Alert kind="error">Halaman ini untuk admin sahaja.</Alert>;
  return <AdminHome />;
}

const TABS = [
  { id: "statistik", label: "📊 Statistik" },
  { id: "laporan", label: "🐞 Laporan & ralat" },
  { id: "pesanan", label: "💳 Pesanan & bayaran" },
  { id: "audio", label: "🎙️ Audio Tadika" },
] as const;

/** Admin home: statistics first, then problem reports, then the payment/affiliate/invoice tools. */
function AdminHome() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((x) => x.id === params.get("tab"))?.id ?? "statistik";
  const [open, setOpen] = useState<number | null>(null);
  const [waiting, setWaiting] = useState<number | null>(null);
  useEffect(() => {
    api<{ openByKind: Record<string, number> }>("/api/admin/reports?status=open&kind=all")
      .then((r) => setOpen(Object.values(r.openByKind).reduce((a, b) => a + b, 0))).catch(() => undefined);
    api<{ stats: Stats }>("/api/admin/orders?status=submitted&limit=1").then((r) => setWaiting(r.stats.awaiting_verification)).catch(() => undefined);
  }, []);
  const badge = (id: string) => (id === "laporan" ? open : id === "pesanan" ? waiting : null);
  return (
    <>
      <PageHeader title="Admin JomCerdik" subtitle="Statistik pelawat, laporan masalah, bayaran dan affiliate. Hanya admin boleh lihat halaman ini." />
      <div className="mb-6 flex flex-wrap gap-2 border-b border-surface-line pb-3" role="tablist">
        {TABS.map((x) => (
          <button key={x.id} type="button" role="tab" aria-selected={tab === x.id} onClick={() => setParams({ tab: x.id }, { replace: true })}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === x.id ? "bg-brand text-white" : "border border-surface-line"}`}>
            {x.label}
            {badge(x.id) ? <span className="ml-1.5 rounded-full bg-accent px-1.5 text-xs font-bold text-white">{badge(x.id)}</span> : null}
          </button>
        ))}
      </div>
      {tab === "statistik" && <StatsPanel />}
      {tab === "laporan" && <ReportsPanel onCount={setOpen} />}
      {tab === "pesanan" && <AdminQueue />}
      {tab === "audio" && <AudioPanel />}
    </>
  );
}

function AdminQueue() {
  const [orders, setOrders] = useState<AdminOrder[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    api<{ orders: AdminOrder[]; stats: Stats }>("/api/admin/orders?status=submitted")
      .then((r) => {
        setOrders(r.orders);
        setStats(r.stats);
      })
      .catch((e) => setError(errorMessage(e)));
  }, []);
  useEffect(load, [load]);

  return (
    <>
      <CardGrid>
        <FeatureCard
          title="Pembelian & Lesen"
          lines={
            stats
              ? [`RM69 terjual: ${stats.tier1_sold} / 100`, `RM79 terjual: ${stats.tier2_sold} / 100`, `Slot ditahan: ${stats.early_held}`]
              : ["Memuatkan…"]
          }
        />
        <FeatureCard title="Kandungan KPM" lines={["Versi kurikulum, SK / SP", "Semakan guru (Fasa 8)"]} />
        <FeatureCard title="Kos & Affiliate" lines={["Kredit AI / penggunaan", "Komisen dan bayaran affiliate di bawah"]} />
      </CardGrid>

      <section className="mt-8">
        <h2 className="text-lg font-semibold">
          Menunggu pengesahan bayaran {stats ? `(${stats.awaiting_verification})` : ""}
        </h2>
        <p className="mb-3 text-sm text-muted">
          Sahkan hanya selepas anda nampak transaksi dengan <strong>jumlah tepat</strong> dalam app MAE. Jangan bergantung pada screenshot.
        </p>
        {error && <Alert kind="error">{error}</Alert>}
        {notice && <Alert kind="success">{notice}</Alert>}
        {orders?.length === 0 && <Alert kind="info">Tiada pesanan menunggu pengesahan.</Alert>}
        <ul className="space-y-3">
          {orders?.map((o) => (
            <OrderRow
              key={o.id}
              o={o}
              onDone={(msg) => {
                setNotice(msg);
                load();
              }}
            />
          ))}
        </ul>
      </section>
      <PaidOrders />
      <Payouts />
      <Invoices />
      <CompanyForm />
    </>
  );
}

type PaidOrder = { id: string; reference_code: string; buyer_email: string; kind?: string; price_tier: string; amount_due_sen: number; paid_at: string | null };

/** Recently confirmed orders: resend the activation email when a buyer lost it or it expired (72 h). */
function PaidOrders() {
  const [rows, setRows] = useState<PaidOrder[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => { api<{ orders: PaidOrder[] }>("/api/admin/orders?status=paid&limit=20").then((r) => setRows(r.orders)).catch((e) => setMsg(errorMessage(e))); }, []);
  async function resend(o: PaidOrder) {
    setBusy(o.id); setMsg(null);
    try {
      const r = await api<{ emailSent: boolean }>(`/api/admin/orders/${o.id}/reissue-invite`, { method: "POST", json: {} });
      setMsg(r.emailSent ? `Jemputan baharu dihantar ke ${o.buyer_email}.` : `Jemputan dicipta tetapi emel ke ${o.buyer_email} gagal dihantar.`);
    } catch (e) { setMsg(errorMessage(e)); } finally { setBusy(null); }
  }
  return (
    <section className="mt-10">
      <h2 className="text-lg font-semibold">Pesanan dibayar (terkini)</h2>
      <p className="mb-3 text-sm text-muted">Pembeli tak jumpa emel aktivasi atau link tamat (72 jam)? Hantar semula di sini.</p>
      {msg && <div className="mb-3"><Alert kind="info">{msg}</Alert></div>}
      {rows?.length === 0 && <p className="text-sm text-muted">Belum ada pesanan dibayar.</p>}
      <ul className="divide-y divide-surface-line text-sm">
        {rows?.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span><span className="font-mono">{o.reference_code}</span> · {o.buyer_email} · {formatMYR(o.amount_due_sen)} · {dt(o.paid_at)}</span>
            {o.kind === "topup" || o.price_tier === "topup"
              ? <span className="text-xs text-muted">Topup kredit</span>
              : <Button variant="secondary" onClick={() => resend(o)} busy={busy === o.id}>Hantar semula jemputan</Button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

type AdminPayout = {
  id: string; amount_sen: number; status: string; requested_at: string; paid_at: string | null; paid_reference: string | null;
  decided_reason: string | null; recipient: { bank?: string; holder?: string; account?: string } | null; referral_code: string; email: string; has_receipt: boolean;
};
type PayoutStats = { affiliates: number; pending_sen: number; requested_sen: number; paid_sen: number };

/** Affiliate payout requests: transfer by hand in MAE, then record the reference. */
function Payouts() {
  const [rows, setRows] = useState<AdminPayout[] | null>(null);
  const [stats, setStats] = useState<PayoutStats | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => {
    api<{ payouts: AdminPayout[]; stats: PayoutStats }>("/api/admin/payouts").then((r) => { setRows(r.payouts); setStats(r.stats); }).catch((e) => setMsg(errorMessage(e)));
  }, []);
  useEffect(load, [load]);
  async function reject(p: AdminPayout) {
    const reason = window.prompt("Sebab tolak (contoh: nombor akaun tidak sah). Baki akan dipulangkan kepada affiliate.");
    if (!reason) return;
    try {
      await api(`/api/admin/payouts/${p.id}/reject`, { method: "POST", json: { reason } });
      setMsg(`Permintaan ${p.email} ditolak; baki dipulangkan.`);
      load();
    } catch (e) { setMsg(errorMessage(e)); }
  }
  async function viewReceipt(id: string) {
    const res = await fetch(`/api/admin/payouts/${id}/receipt`);
    if (!res.ok) { setMsg("Resit tidak dapat dibuka."); return; }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await res.blob());
    a.download = `resit-${id.slice(0, 8)}`;
    a.click();
  }
  const open = rows?.filter((p) => p.status === "requested" || p.status === "approved") ?? [];
  const done = rows?.filter((p) => p.status !== "requested" && p.status !== "approved") ?? [];
  return (
    <section className="mt-10">
      <h2 className="text-lg font-semibold">Bayaran affiliate {open.length ? `(${open.length} menunggu)` : ""}</h2>
      {stats && (
        <p className="mb-3 text-sm text-muted">
          {stats.affiliates} affiliate · komisen tertahan {formatMYR(stats.pending_sen)} · diminta {formatMYR(stats.requested_sen)} · sudah dibayar {formatMYR(stats.paid_sen)}
        </p>
      )}
      {msg && <div className="mb-3"><Alert kind="info">{msg}</Alert></div>}
      {open.length === 0 && <p className="text-sm text-muted">Tiada permintaan bayaran.</p>}
      <ul className="space-y-3">
        {open.map((p) => (
          <li key={p.id} className="rounded-card border border-surface-line p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-2xl font-bold tabular-nums">{formatMYR(p.amount_sen)}</p>
              <p className="text-sm">{p.email} · <span className="font-mono">{p.referral_code}</span></p>
            </div>
            <BankLine r={p.recipient} />
            <p className="text-xs text-muted">Diminta {dt(p.requested_at)}</p>
            <PayForm p={p} onDone={(m) => { setMsg(m); load(); }} onReject={() => reject(p)} />
          </li>
        ))}
      </ul>
      {done.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm text-muted">
          {done.slice(0, 10).map((p) => (
            <li key={p.id}>
              {dt(p.requested_at)} · {p.email} · {formatMYR(p.amount_sen)} · {p.status === "paid" ? `dibayar${p.paid_reference ? ` (${p.paid_reference})` : ""}` : `ditolak: ${p.decided_reason}`}
              {p.has_receipt && <button type="button" className="ml-2 text-brand underline" onClick={() => viewReceipt(p.id)}>resit</button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function OrderRow({ o, onDone }: { o: AdminOrder; onDone: (msg: string) => void }) {
  const [bankRef, setBankRef] = useState(o.payer_reference ?? "");
  const [busy, setBusy] = useState<"confirm" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(kind: "confirm" | "reject") {
    let reason: string | null = null;
    if (kind === "reject") {
      reason = window.prompt("Sebab tolak (contoh: tiada transaksi sepadan)");
      if (!reason) return;
    } else if (!window.confirm(`Sahkan ${formatMYR(o.amount_due_sen)} daripada ${o.buyer_email}? Ini tidak boleh dibatalkan.`)) {
      return;
    }
    setBusy(kind);
    setError(null);
    try {
      const r = await api<{ emailSent?: boolean; invoiceNo?: string | null }>(`/api/admin/orders/${o.id}/${kind}`, {
        method: "POST",
        json: kind === "confirm" ? { bankReference: bankRef } : { reason },
      });
      onDone(
        kind === "confirm"
          ? `${o.reference_code} disahkan${r.invoiceNo ? `, invois ${r.invoiceNo} dikeluarkan` : ""}.${r.emailSent === false ? " Emel gagal dihantar — gunakan 'Hantar semula jemputan'." : " Emel dihantar bersama invois."}`
          : `${o.reference_code} ditolak. Slot Early Bird dilepaskan.`,
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <li className="rounded-card border border-surface-line p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-2xl font-bold tabular-nums">{formatMYR(o.amount_due_sen)}</p>
        <p className="font-mono text-sm">{o.reference_code}</p>
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        <div><dt className="inline text-muted">Emel: </dt><dd className="inline">{o.buyer_email}</dd></div>
        <div><dt className="inline text-muted">Pembayar: </dt><dd className="inline">{o.payer_name ?? "—"}</dd></div>
        <div><dt className="inline text-muted">Pakej: </dt><dd className="inline">{o.price_tier === "topup" ? "Tambah kertas AI (RM50 → 60 kertas)" : o.price_tier}</dd></div>
        <div><dt className="inline text-muted">Dihantar: </dt><dd className="inline">{dt(o.submitted_at)}</dd></div>
      </dl>
      {o.payment_proof_key ? <ProofPreview orderId={o.id} /> : <p className="mt-2 text-xs text-muted">Tiada bukti bayaran dilampirkan.</p>}
      <label className="mt-3 block text-sm">
        <span className="text-muted">Rujukan bank (seperti dalam MAE)</span>
        <input
          value={bankRef}
          onChange={(e) => setBankRef(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-surface-line px-3 py-2 font-mono"
        />
      </label>
      {error && <div className="mt-2"><Alert kind="error">{error}</Alert></div>}
      <div className="mt-3 flex gap-2">
        <Button onClick={() => act("confirm")} busy={busy === "confirm"} disabled={bankRef.trim().length < 4 || busy !== null}>
          Sahkan bayaran
        </Button>
        <Button variant="secondary" onClick={() => act("reject")} busy={busy === "reject"} disabled={busy !== null}>
          Tolak
        </Button>
      </div>
    </li>
  );
}

function BankLine({ r }: { r: AdminPayout["recipient"] }) {
  const [copied, setCopied] = useState(false);
  const acc = r?.account ?? "";
  return (
    <p className="mt-2 text-sm">
      <strong>{r?.bank}</strong> · {r?.holder} · <span className="font-mono text-base">{acc}</span>
      <button type="button" className="ml-2 text-brand underline" onClick={async () => { try { await navigator.clipboard.writeText(acc); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* blocked */ } }}>
        {copied ? "disalin ✓" : "salin no. akaun"}
      </button>
    </p>
  );
}

/** Upload the MAE transfer receipt and confirm; the affiliate is emailed the receipt. */
function PayForm({ p, onDone, onReject }: { p: AdminPayout; onDone: (msg: string) => void; onReject: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ERR: Record<string, string> = {
    receipt_required: "Pilih fail resit dahulu.",
    receipt_too_large: "Resit terlalu besar (maksimum 5 MB).",
    receipt_bad_type: "Resit mesti gambar (JPG/PNG/WEBP) atau PDF.",
  };
  async function submit() {
    if (!file) { setError(ERR.receipt_required!); return; }
    if (!window.confirm(`Sahkan ${formatMYR(p.amount_sen)} sudah dipindahkan kepada ${p.recipient?.holder ?? p.email}? Resit akan diemel kepada affiliate.`)) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("receipt", file);
      fd.append("reference", reference);
      const res = await fetch(`/api/admin/payouts/${p.id}/paid`, { method: "POST", body: fd });
      const data = (await res.json().catch(() => ({}))) as { error?: string; emailSent?: boolean };
      if (!res.ok) throw new Error(ERR[data.error ?? ""] ?? errorMessage(new ApiError(res.status, data.error ?? "network_error")));
      onDone(`Bayaran ${formatMYR(p.amount_sen)} kepada ${p.email} direkod.${data.emailSent === false ? " Emel resit gagal dihantar, tapi affiliate boleh lihat resit di dashboard." : " Resit telah diemel."}`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <div className="mt-3 space-y-2 border-t border-surface-line pt-3">
      <label className="block text-sm">
        <span className="text-muted">Resit pindahan (gambar / PDF)</span>
        <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="mt-1 block w-full text-sm" />
      </label>
      <label className="block text-sm">
        <span className="text-muted">Rujukan MAE (pilihan)</span>
        <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={64} className="mt-1 block w-full rounded-lg border border-surface-line bg-card px-3 py-2 font-mono" />
      </label>
      {error && <Alert kind="error">{error}</Alert>}
      <div className="flex gap-2">
        <Button onClick={submit} busy={busy} disabled={!file}>Sahkan dah bayar</Button>
        <Button variant="secondary" onClick={onReject} disabled={busy}>Tolak</Button>
      </div>
    </div>
  );
}

/** Downloads a private file (admin-only route) and offers it as a file download. */
async function downloadPrivate(url: string, filename: string): Promise<boolean> {
  const res = await fetch(url);
  if (!res.ok) return false;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await res.blob());
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  return true;
}

/** Buyer's payment proof, shown inline (image) or as a download (PDF). */
function ProofPreview({ orderId }: { orderId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [type, setType] = useState("");
  const [err, setErr] = useState(false);
  async function show() {
    const res = await fetch(`/api/admin/orders/${orderId}/proof`);
    if (!res.ok) { setErr(true); return; }
    const blob = await res.blob();
    setType(blob.type);
    setUrl(URL.createObjectURL(blob));
  }
  if (err) return <p className="mt-2 text-xs text-red-700">Bukti bayaran tidak dapat dibuka.</p>;
  if (!url) return <button type="button" className="mt-2 text-sm text-brand underline" onClick={show}>Lihat bukti bayaran pembeli</button>;
  return type === "application/pdf"
    ? <a className="mt-2 block text-sm text-brand underline" href={url} download={`bukti-${orderId.slice(0, 8)}.pdf`}>Muat turun bukti bayaran (PDF)</a>
    : <img src={url} alt="Bukti bayaran pembeli" className="mt-2 max-h-96 rounded-lg border border-surface-line" />;
}

type InvoiceRow = { invoice_no: string; issued_at: string; buyer_email: string; description: string; amount_sen: number; bank_reference: string | null; order_id: string; reference_code: string; payer_name: string | null; has_proof: boolean };

/** Every invoice issued, with the buyer's proof — for the bank or accounts. */
function Invoices() {
  const [rows, setRows] = useState<InvoiceRow[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { api<{ invoices: InvoiceRow[] }>("/api/admin/invoices").then((r) => setRows(r.invoices)).catch((e) => setMsg(errorMessage(e))); }, []);
  const total = rows?.reduce((a, r) => a + r.amount_sen, 0) ?? 0;
  return (
    <section className="mt-10">
      <h2 className="text-lg font-semibold">Invois & resit pembeli {rows ? `(${rows.length})` : ""}</h2>
      <p className="mb-3 text-sm text-muted">Disimpan secara automatik untuk rekod bank. Jumlah: {formatMYR(total)}.</p>
      {msg && <Alert kind="error">{msg}</Alert>}
      {rows?.length === 0 && <p className="text-sm text-muted">Belum ada invois. Invois dikeluarkan bila anda sahkan bayaran.</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-muted"><tr><th className="py-1 pr-3">Invois</th><th className="pr-3">Tarikh</th><th className="pr-3">Pembeli</th><th className="pr-3">Jumlah</th><th className="pr-3">Rujukan</th><th>Fail</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.invoice_no} className="border-t border-surface-line align-top">
                  <td className="py-1.5 pr-3 font-mono">{r.invoice_no}</td>
                  <td className="pr-3">{dt(r.issued_at)}</td>
                  <td className="pr-3">{r.buyer_email}{r.payer_name ? <span className="block text-xs text-muted">{r.payer_name}</span> : null}</td>
                  <td className="pr-3 tabular-nums">{formatMYR(r.amount_sen)}</td>
                  <td className="pr-3 font-mono text-xs">{r.reference_code}{r.bank_reference ? <span className="block">{r.bank_reference}</span> : null}</td>
                  <td className="space-x-2 whitespace-nowrap">
                    <button type="button" className="text-brand underline" onClick={() => downloadPrivate(`/api/admin/invoices/${r.invoice_no}/pdf`, `${r.invoice_no}.pdf`).then((ok) => ok || setMsg("Invois tidak dapat dimuat turun."))}>invois</button>
                    {r.has_proof && <button type="button" className="text-brand underline" onClick={() => downloadPrivate(`/api/admin/orders/${r.order_id}/proof`, `bukti-${r.reference_code}`).then((ok) => ok || setMsg("Bukti tidak dapat dimuat turun."))}>bukti bayaran</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type Company = { name: string; reg_no: string; address: string; email: string; phone: string };

/** Seller details printed on new invoices. */
function CompanyForm() {
  const [c, setC] = useState<Company | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api<{ company: Company | null }>("/api/admin/company").then((r) => setC({ name: "Kroni AI Empire", reg_no: "", address: "", email: "", phone: "", ...(r.company ?? {}) })).catch((e) => setMsg(errorMessage(e))); }, []);
  if (!c) return null;
  const set = (k: keyof Company) => (e: { target: { value: string } }) => setC({ ...c, [k]: e.target.value });
  async function save() {
    setBusy(true); setMsg(null);
    try { await api("/api/admin/company", { method: "PUT", json: c }); setMsg("Disimpan. Invois baharu akan guna butiran ini."); }
    catch (e) { setMsg(errorMessage(e)); } finally { setBusy(false); }
  }
  const input = "mt-1 block w-full rounded-lg border border-surface-line bg-card px-3 py-2";
  return (
    <section className="mt-10 rounded-card border border-surface-line p-4">
      <h2 className="text-lg font-semibold">Butiran syarikat pada invois</h2>
      <p className="mb-3 text-sm text-muted">Ruang kosong tidak dicetak. Invois lama kekal dengan butiran semasa ia dikeluarkan.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">Nama syarikat (seperti SSM)<input className={input} value={c.name} onChange={set("name")} /></label>
        <label className="text-sm">No. pendaftaran SSM<input className={input} value={c.reg_no} onChange={set("reg_no")} /></label>
        <label className="text-sm">Telefon (pilihan)<input className={input} value={c.phone} onChange={set("phone")} /></label>
      </div>
      {msg && <div className="mt-3"><Alert kind="info">{msg}</Alert></div>}
      <Button className="mt-3" onClick={save} busy={busy} disabled={c.name.trim().length < 2}>Simpan butiran syarikat</Button>
    </section>
  );
}
